import { from, Observable } from "rxjs";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { find } from "lodash-es";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { UUID, uuid } from "src/app/support/uuid";
import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";
import { PageRequest } from "src/app/support/page-request";
import { Paginated } from "src/app/support/paginated";
import { Injectable } from "@angular/core";
import { AppointmentReason } from "../models/appointment-reason/appointment-reason";
import { AppointmentReasonCreate } from "../models/appointment-reason/appointment-reason.create";
import { AppointmentReasonDelete } from "../models/appointment-reason/appointment-reason.delete";
import { AppointmentReasonLoad } from "../models/appointment-reason/appointment-reason.load";
import { AppointmentReasonUpdate } from "../models/appointment-reason/appointment-reason.update";

const DB_NAME = "appointment-reason";

@Injectable()
export class AppointmentReasonBackend {
  load(request: AppointmentReasonLoad): Observable<AppointmentReason> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return find(list, (x) => x.id === request.id)!;
      })
    );
  }

  list(request: PageRequest): Observable<Paginated<AppointmentReason>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return <Paginated<AppointmentReason>>{
          list,
          count: list.length
        };
      })
    );
  }

  @UserNotify({
    successMessage: marker("Nuovo motivo di presentazione"),
    errorMessage: marker("Impossibile creare il motivo di presentazione")
  })
  create(request: AppointmentReasonCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((appointment: AppointmentReason[]) => {
        const newUuid = uuid();

        const newAppointmentReason: Partial<AppointmentReason> = {
          id: newUuid,
          ...request
        };

        return from(
          writeDemoStorage(DB_NAME, [...appointment, newAppointmentReason])
        ).pipe(map(() => newUuid));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Motivo di presentazione aggiornato"),
    errorMessage: marker("Impossibile aggiornare motivo di presentazione")
  })
  update(request: AppointmentReasonUpdate): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.id === request.id) {
                return {
                  ...x,
                  ...request
                };
              }
              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Motivo di presentazione rimosso"),
    errorMessage: marker("Impossibile rimuovere motivo di presentazione")
  })
  delete(request: AppointmentReasonDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => x.id !== request.id)
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<AppointmentReason[]> {
  return (await loadDemoStorage<AppointmentReason[]>(DB_NAME)) || [];
}
